/**
 * Utility functions for determining node hierarchy in reports.
 * Used by both ReportSidebar and ReportPage to ensure consistent behavior.
 *
 * Handles all scenarios:
 * - Only ideas (with nested hierarchies)
 * - Only unmet needs
 * - Only ventures
 * - Any combination of the above
 */

/**
 * Generic node interface for report hierarchy calculations.
 * Works with both ReactFlow nodes and backend MindMapNodes.
 */
export interface ReportNode {
  id: string
  type?: string
  data: {
    category?: string
    [key: string]: unknown
  }
}

/**
 * Generic edge interface for report hierarchy calculations.
 */
export interface ReportEdge {
  id: string
  source: string
  target: string
}

/**
 * Configuration for hierarchy calculations
 */
export interface HierarchyConfig<TNode extends ReportNode> {
  nodes: TNode[]
  edges: ReportEdge[]
  /** Function to get effective node type (handles summarized nodes) */
  getEffectiveNodeType: (node: TNode) => string
  /** Function to check if node should be displayed (for filtering) */
  shouldDisplayNode?: (nodeId: string) => boolean
}

/**
 * Build a parent map from edges (child ID -> parent ID)
 */
function buildParentMap(edges: ReportEdge[]): Map<string, string> {
  const map = new Map<string, string>()
  edges.forEach((edge) => {
    map.set(edge.target, edge.source)
  })
  return map
}

/**
 * Build a children map from edges (parent ID -> child IDs)
 */
function buildChildrenMap(edges: ReportEdge[]): Map<string, string[]> {
  const map = new Map<string, string[]>()
  edges.forEach((edge) => {
    const children = map.get(edge.source) ?? []
    children.push(edge.target)
    map.set(edge.source, children)
  })
  return map
}

/**
 * Recursively collect all descendant IDs of a node that match a predicate.
 * Uses cycle detection to prevent infinite loops.
 */
function collectDescendants<TNode extends ReportNode>(
  parentId: string,
  config: HierarchyConfig<TNode>,
  childrenMap: Map<string, string[]>,
  nodeMap: Map<string, TNode>,
  predicate: (node: TNode) => boolean,
  visited: Set<string> = new Set()
): Set<string> {
  const result = new Set<string>()

  if (visited.has(parentId)) return result
  visited.add(parentId)

  const childIds = childrenMap.get(parentId) ?? []
  for (const childId of childIds) {
    const child = nodeMap.get(childId)
    if (!child) continue

    // Check display filter if provided
    if (config.shouldDisplayNode && !config.shouldDisplayNode(childId)) continue

    if (predicate(child)) {
      result.add(childId)
      // Recursively collect from this child
      const childDescendants = collectDescendants(
        childId,
        config,
        childrenMap,
        nodeMap,
        predicate,
        visited
      )
      childDescendants.forEach((id) => result.add(id))
    }
  }

  return result
}

/**
 * Get all root-level standalone ideas.
 *
 * A "root standalone idea" is an idea that:
 * 1. Is NOT a descendant of any unmet need (ideas under needs render in the need section)
 * 2. Has NO parent that is also a standalone idea (those render recursively)
 *
 * This handles:
 * - Ideas with no parent at all
 * - Ideas whose parent is a venture (not a need or another idea)
 * - Nested idea hierarchies (only the root of each tree is returned)
 */
export function getRootStandaloneIdeas<TNode extends ReportNode>(
  config: HierarchyConfig<TNode>
): TNode[] {
  const { nodes, edges, getEffectiveNodeType, shouldDisplayNode } = config

  // Build lookup maps
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  const parentMap = buildParentMap(edges)
  const childrenMap = buildChildrenMap(edges)

  // Find all job nodes
  const needNodes = nodes.filter((node) => {
    const effectiveType = getEffectiveNodeType(node)
    return effectiveType === 'needNode' && (shouldDisplayNode ? shouldDisplayNode(node.id) : true)
  })

  // Collect ALL ideas that are descendants of needs (at any depth)
  const ideasUnderNeeds = new Set<string>()
  for (const need of needNodes) {
    const descendants = collectDescendants(
      need.id,
      config,
      childrenMap,
      nodeMap,
      (node) => getEffectiveNodeType(node) === 'ideaNode'
    )
    descendants.forEach((id) => ideasUnderNeeds.add(id))
  }

  // Get all idea nodes that are NOT under jobs
  const ideasNotUnderNeeds = nodes.filter((node) => {
    const effectiveType = getEffectiveNodeType(node)
    if (effectiveType !== 'ideaNode') return false
    if (shouldDisplayNode && !shouldDisplayNode(node.id)) return false
    if (ideasUnderNeeds.has(node.id)) return false
    return true
  })

  // Create a set of standalone idea IDs for quick lookup
  const standaloneIdeaIds = new Set(ideasNotUnderNeeds.map((n) => n.id))

  // Filter to only ROOT standalone ideas
  // A root standalone idea has no parent OR its parent is not also a standalone idea
  return ideasNotUnderNeeds.filter((idea) => {
    const parentId = parentMap.get(idea.id)

    // No parent = definitely a root
    if (!parentId) return true

    // If parent is also a standalone idea, this will be rendered as a child
    if (standaloneIdeaIds.has(parentId)) return false

    // Parent exists but is not a standalone idea (e.g., venture, ghost)
    // This idea should be rendered at root level
    return true
  })
}

/**
 * Get child nodes of a parent, with deduplication.
 * Handles the display filter and returns only visible children.
 */
function getChildNodes<TNode extends ReportNode>(
  parentId: string,
  config: HierarchyConfig<TNode>
): TNode[] {
  const { nodes, edges, shouldDisplayNode } = config

  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  const childEdges = edges.filter((edge) => edge.source === parentId)

  const childNodes = childEdges
    .map((edge) => nodeMap.get(edge.target))
    .filter((node): node is TNode => node !== undefined)
    .filter((node) => (shouldDisplayNode ? shouldDisplayNode(node.id) : true))

  // Deduplicate by node id
  return Array.from(new Map(childNodes.map((node) => [node.id, node])).values())
}

/**
 * Filter nodes by effective type.
 * Handles summarized nodes by checking their original type.
 */
export function filterNodesByType<TNode extends ReportNode>(
  nodes: TNode[],
  targetType: string,
  config: Pick<HierarchyConfig<TNode>, 'getEffectiveNodeType' | 'shouldDisplayNode'>
): TNode[] {
  const { getEffectiveNodeType, shouldDisplayNode } = config
  return nodes.filter((node) => {
    const effectiveType = getEffectiveNodeType(node)
    if (effectiveType !== targetType) return false
    if (shouldDisplayNode && !shouldDisplayNode(node.id)) return false
    return true
  })
}

// ============================================================================
// HIERARCHICAL REPORT HELPERS
// These functions support the grouped report view:
// Venture → Unmet needs → Ideas
// ============================================================================

/**
 * Get direct child unmet-need nodes of a parent (typically a venture node).
 */
export function getChildNeeds<TNode extends ReportNode>(
  parentId: string,
  config: HierarchyConfig<TNode>
): TNode[] {
  const { getEffectiveNodeType } = config
  return getChildNodes(parentId, config).filter((node) => {
    const effectiveType = getEffectiveNodeType(node)
    return effectiveType === 'needNode'
  })
}

/**
 * Get "root" unmet needs - needs that are NOT children of any venture.
 * These are standalone unmet needs that should be rendered at the top level.
 */
export function getRootNeeds<TNode extends ReportNode>(config: HierarchyConfig<TNode>): TNode[] {
  const { nodes, edges, getEffectiveNodeType, shouldDisplayNode } = config

  const parentMap = buildParentMap(edges)
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))

  // Get all venture IDs
  const ventureIds = new Set(
    nodes
      .filter((node) => {
        const effectiveType = getEffectiveNodeType(node)
        return effectiveType === 'ventureNode'
      })
      .map((n) => n.id)
  )

  // Get all need nodes
  const needNodes = nodes.filter((node) => {
    const effectiveType = getEffectiveNodeType(node)
    if (effectiveType !== 'needNode') return false
    if (shouldDisplayNode && !shouldDisplayNode(node.id)) return false
    return true
  })

  // Filter to only needs whose parent is NOT a venture
  return needNodes.filter((need) => {
    const parentId = parentMap.get(need.id)
    // No parent = standalone
    if (!parentId) return true
    // Parent is a venture = NOT standalone (will render under venture)
    if (ventureIds.has(parentId)) return false
    // Parent is something else (ghost node, etc.) = treat as standalone
    const parentNode = nodeMap.get(parentId)
    if (!parentNode) return true
    const parentType = getEffectiveNodeType(parentNode)
    return parentType !== 'ventureNode'
  })
}
